import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { useSync } from "@api";
import { phases, tips } from "./constants";

import { SyncSteps } from "./SyncSteps";
import { SyncProgressSection } from "./SyncProgressSection";
import { SyncSkeletons } from "./SyncSkeletons";
import { SyncFooter } from "./SyncFooter";
import { SyncLogsAccordion } from "./SyncLogsAccordion";

export function Sync() {
  const { triggerSync, cancelSync, message, loading, isSuccess, error, reset } =
    useSync();

  const [progress, setProgress] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    let progInterval: NodeJS.Timeout | null = null;
    let tipInterval: NodeJS.Timeout | null = null;

    if (loading) {
      setProgress(0);
      setLogs([]);

      progInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 2, 95));
      }, 120);

      tipInterval = setInterval(() => {
        setTipIndex((p) => (p + 1) % tips.length);
        setLogs((l) => [
          ...l,
          `[${new Date().toLocaleTimeString()}] ${tips[Math.floor(Math.random() * tips.length)]}`,
        ]);
      }, 1500);
    } else {
      if (isSuccess) {
        setProgress(100);
        setLastSync(new Date().toLocaleString());
        toast.current?.show({
          severity: "success",
          summary: "Synchronization Complete",
          detail: message || "Your data is up to date.",
          life: 4000,
        });
      }

      if (error) {
        setProgress(0);
        toast.current?.show({
          severity: "error",
          summary: "Synchronization Failed",
          detail: error.message,
          life: 5000,
        });
      }

      if (progInterval) clearInterval(progInterval);
      if (tipInterval) clearInterval(tipInterval);
    }

    return () => {
      if (progInterval) clearInterval(progInterval);
      if (tipInterval) clearInterval(tipInterval);
    };
  }, [loading, isSuccess, error, message]);

  const currentPhaseIndex = Math.min(
    Math.floor((progress / 100) * phases.length),
    phases.length - 1,
  );

  const handleStart = () => {
    reset();
    triggerSync();
  };

  const handleCancel = () => {
    cancelSync();
    reset();
    setProgress(0);
    toast.current?.show({
      severity: "warn",
      summary: "Synchronization Cancelled",
      detail: "The request was aborted by the user.",
      life: 3000,
    });
  };

  return (
    <div className="flex justify-content-center align-items-center w-full px-3 py-5 h-full">
      <Toast ref={toast} />
      <div
        className="flex flex-column gap-5 surface-card border-1 border-round shadow-2 p-5 w-full"
        style={{ maxWidth: "70%" }}
      >
        <h2 className="text-primary text-center m-0">Data Synchronization</h2>

        <SyncSteps currentPhaseIndex={currentPhaseIndex} />
        <SyncProgressSection
          progress={progress}
          loading={loading}
          currentTip={tips[tipIndex]}
        />
        {loading && <SyncSkeletons />}
        <SyncFooter
          loading={loading}
          isSuccess={isSuccess}
          error={error}
          message={message}
          onStart={handleStart}
          onCancel={handleCancel}
        />
        <SyncLogsAccordion logs={logs} />

        {lastSync && !loading && (
          <small className="text-500 text-center">
            Last sync: <strong>{lastSync}</strong>
          </small>
        )}
      </div>
    </div>
  );
}
